import { useEffect, useState } from "react";
import { Play, Loader2, FileJson, ChevronDown, ChevronRight, AlertTriangle, CircleSlash, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt } from "@/lib/catalog";

import { tr } from "@/lib/i18n";
const STATUS = {
  incomplete: [tr("Incompleto — dati mancanti"), "bg-slate-100 text-slate-700 border-slate-300"],
  preliminary: [tr("Preliminare — dati non validati"), "bg-amber-50 text-amber-800 border-amber-300"],
  calculated_pending_review: [tr("Calcolato — in attesa di revisione ingegneristica"), "bg-sky-50 text-sky-800 border-sky-300"],
};

const F = ({ label, children }) => (
  <label className="block"><span className="text-xs text-slate-500">{label}</span><div className="mt-0.5">{children}</div></label>
);
const cls = "w-full h-9 px-2.5 text-sm font-tech border border-slate-200 rounded-md outline-none focus:border-[#0F52BA] bg-white";

const Num = ({ v, on, testid, step = "0.01" }) => (
  <input type="number" step={step} value={v ?? ""} data-testid={testid} className={cls}
    onChange={(e) => on(e.target.value === "" ? null : Number(e.target.value))} />
);

const Pick = ({ v, on, items, testid }) => (
  <select value={v || ""} onChange={(e) => on(e.target.value || null)} data-testid={testid} className={cls}>
    <option value="">{tr("— seleziona —")}</option>
    {items.map((x) => <option key={x.article_code} value={x.article_code}>{x.article_code} · {x.validation_status}</option>)}
  </select>
);

export const EngineInputs = ({ p, update, products, norms }) => {
  const E = p.engine || {};
  const set = (k) => (v) => update((prev) => ({ engine: { ...(prev.engine || {}), [k]: v } }));
  const flat = p.roof_type === "piano";
  const country = E.country || "CH";
  const terr = norms?.[country]?.wind?.terrain || {};
  const of = (c) => products.filter((x) => x.category === c);
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="engine-inputs">
      <F label={tr("Banca dati normativa")}>
        <select value={country} onChange={(e) => set("country")(e.target.value)} className={cls} data-testid="engine-country">
          <option value="CH">{tr("CH — SIA 261 / SN EN")}</option><option value="EN">{tr("EN — Eurocodice senza NA")}</option>
        </select>
      </F>
      {country === "CH"
        ? <F label={tr("qp0 mappa SIA 261 [kN/m²]")}><Num v={E.qp0} on={set("qp0")} testid="engine-qp0" /></F>
        : <F label={tr("vb,0 [m/s]")}><Num v={E.vb0} on={set("vb0")} testid="engine-vb0" step="0.1" /></F>}
      <F label={tr("Categoria di terreno")}>
        <select value={E.terrain || "III"} onChange={(e) => set("terrain")(e.target.value)} className={cls} data-testid="engine-terrain">
          {Object.entries(terr).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
        </select>
      </F>
      <F label={tr("Δh zona neve (SIA 261 Anh. D) [m]")}><Num v={E.snow_dh} on={set("snow_dh")} testid="engine-snow-dh" step="1" /></F>
      <F label={tr("Coeff. esposizione neve Ce")}><Num v={E.Ce ?? 1} on={set("Ce")} testid="engine-ce" step="0.1" /></F>
      <F label={tr("Altezza di riferimento z [m]")}><Num v={E.z ?? p.building_height} on={set("z")} testid="engine-z" step="0.1" /></F>
      {!flat ? (
        <>
          <F label={tr("Interasse travetti [m]")}><Num v={E.rafter_spacing} on={set("rafter_spacing")} testid="engine-rafter" /></F>
          <F label={tr("Binario")}><Pick v={E.rail || "DEMO-RAIL-40"} on={set("rail")} items={of("rail")} testid="engine-rail" /></F>
          <F label={tr("Gancio")}><Pick v={E.hook || "DEMO-HOOK-TG"} on={set("hook")} items={of("roof_hook")} testid="engine-hook" /></F>
          <F label={tr("Vite")}><Pick v={E.screw || "DEMO-SCREW-8x100"} on={set("screw")} items={of("screw")} testid="engine-screw" /></F>
        </>
      ) : (
        <>
          <F label={tr("Sistema tetto piano")}><Pick v={E.flat_system} on={set("flat_system")} items={of("flat_system")} testid="engine-flat-system" /></F>
          <F label={tr("Zavorra")}><Pick v={E.ballast} on={set("ballast")} items={of("ballast")} testid="engine-ballast" /></F>
          <F label={tr("Ancoraggi per modulo (ibrido)")}><Num v={E.anchors_per_module} on={set("anchors_per_module")} testid="engine-anchors" step="0.5" /></F>
          <F label={tr("Ancoraggio")}><Pick v={E.anchor} on={set("anchor")} items={of("anchor")} testid="engine-anchor" /></F>
          <F label={tr("Resistenza isolante [kPa]")}><Num v={E.insulation_kPa} on={set("insulation_kPa")} testid="engine-insulation" step="1" /></F>
          <F label={tr("Portata solaio esistente [kN/m²]")}><Num v={E.roof_capacity_kN_m2} on={set("roof_capacity_kN_m2")} testid="engine-roof-capacity" /></F>
        </>
      )}
    </div>
  );
};

const ResultRow = ({ r, flat, i }) => {
  const [open, setOpen] = useState(false);
  const mu = Math.max(...r.checks.map((c) => c.utilization));
  return (
    <>
      <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`engine-row-${i}`}>
        <td className="py-2 pl-2">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td>{r.field}</td><td className="font-tech">{r.zone}</td><td className="font-tech">{r.n_modules}</td>
        <td className="font-tech">{flat ? `${fmt(r.ballast_kg_per_module, 1)} kg (${r.governing})` : tr("{0} m · {1} ganci/binario", [fmt(r.hook_spacing_m), r.hooks_per_rail])}</td>
        <td className={`font-tech font-semibold ${mu <= 1 ? "text-emerald-700" : "text-amber-700"}`}>{Math.round(mu * 100)}%</td>
        <td>{r.ok ? <span className="text-emerald-700">OK</span> : <span className="text-amber-700">{tr("Non verificato")}</span>}</td>
      </tr>
      {open && (
        <tr><td colSpan={7} className="bg-slate-50 px-4 py-3">
          {!flat && <div className="text-xs font-tech text-slate-500 mb-2">{tr("cpe ↑")} {r.cpe_suction} {tr("· cpe ↓")} {r.cpe_pressure} {tr("· sbalzo")} {r.cantilever_m} m · {Object.entries(r.loads_kN_m2).map(([k, v]) => `${k} ${v}`).join(" · ")} {tr("kN/m²")}</div>}
          {flat && <div className="text-xs font-tech text-slate-500 mb-2">c_up {r.c_up} · c_h {r.c_h} · U {r.U_kN} {tr("kN · H")} {r.H_kN} {tr("kN · G")} {r.G_sys_kN} {tr("kN · σ contatto")} {r.contact_pressure_kPa_char ?? "—"} kPa</div>}
          <table className="w-full text-xs"><tbody>
            {r.checks.map((c) => (
              <tr key={c.check}><td className="py-1">{c.check}</td><td className="font-tech text-slate-500">{c.formula}</td>
                <td className="font-tech">{c.Ed} / {c.Rd} {c.unit}</td>
                <td className={`font-tech font-semibold ${c.ok ? "text-emerald-700" : "text-amber-700"}`}>{Math.round(c.utilization * 100)}%</td></tr>
            ))}
          </tbody></table>
        </td></tr>
      )}
    </>
  );
};

export const EngineReport = ({ p, update, onResult }) => {
  const [products, setProducts] = useState([]);
  const [norms, setNorms] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTrace, setShowTrace] = useState(false);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products)).catch(() => {});
    api.get("/norms").then((r) => setNorms(r.data)).catch(() => {});
  }, []);
  const calc = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${p.id}`, { engine: p.engine || {}, roofs: p.roofs, modules: p.modules, building_height: p.building_height, altitude: p.altitude, roof_type: p.roof_type });
      const { data } = await api.post(`/engine/calculate/${p.id}`);
      setRes(data); onResult?.(data);
    } catch { toast.error(tr("Calcolo non riuscito")); } finally { setBusy(false); }
  };
  const exportJSON = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(res, null, 2)], { type: "application/json" }));
    a.download = `calcolo_${res.input_hash}.json`; a.click();
  };
  const flat = p.roof_type === "piano";
  const st = res && STATUS[res.status];
  return (
    <div className="space-y-4" data-testid="engine-report">
      <EngineInputs p={p} update={update} products={products} norms={norms} />
      <div className="flex flex-wrap gap-2">
        <button onClick={calc} disabled={busy} data-testid="engine-run-btn" className="h-10 px-4 rounded-md bg-[#0F52BA] text-white text-sm flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}{tr("Esegui calcolo strutturale (backend)")}
        </button>
        {res && <button onClick={exportJSON} data-testid="engine-export-json" className="h-10 px-4 rounded-md border border-slate-200 text-sm flex items-center gap-2"><FileJson className="w-4 h-4" />{tr("Traccia di calcolo JSON")}</button>}
      </div>
      {res && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-3 py-1.5 rounded-md border text-sm font-medium ${st[1]}`} data-testid="engine-status">{st[0]}</span>
            <span className="px-3 py-1.5 rounded-md border border-slate-300 text-sm flex items-center gap-1.5" data-testid="engine-no-approval"><CircleSlash className="w-4 h-4" />{tr("Nessuna approvazione strutturale emessa")}</span>
            <span className="text-xs font-tech text-slate-500">{res.engine_version} · {res.norms_version} {tr("· hash")} {res.input_hash}</span>
          </div>
          {res.missing.length > 0 && (
            <div className="rounded-md border border-slate-300 bg-slate-50 p-3 text-sm" data-testid="engine-missing">
              <div className="font-medium mb-1 flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" />{tr("Dati mancanti (")}{res.missing.length})</div>
              <ul className="list-disc pl-5 space-y-0.5">{res.missing.map((m) => <li key={m.field}><span className="font-tech">{m.field}</span>: {m.reason}</li>)}</ul>
            </div>
          )}
          {res.warnings.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="engine-warnings">
              {res.warnings.map((w) => <div key={w} className="flex gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{w}</div>)}
            </div>
          )}
          {res.results.length > 0 && (
            <table className="w-full text-sm" data-testid="engine-results-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400"><th /><th>{tr("Campo")}</th><th>{tr("Zona")}</th><th>{tr("Moduli")}</th><th>{flat ? tr("Zavorra/modulo") : tr("Interasse ottimizzato")}</th><th>{tr("Sfrutt. max")}</th><th>{tr("Esito")}</th></tr></thead>
              <tbody>{res.results.map((r, i) => <ResultRow key={i} r={r} flat={flat} i={i} />)}</tbody>
            </table>
          )}
          <div className="text-xs font-tech text-slate-500" data-testid="engine-bom">{tr("Distinta dal motore:")} {Object.entries(res.bom).map(([k, v]) => `${k} ${typeof v === "number" ? fmt(v, k.endsWith("_m") || k.endsWith("kg") ? 1 : 0) : v}`).join(" · ")}</div>
          <button onClick={() => setShowTrace(!showTrace)} className="text-sm text-[#0F52BA]" data-testid="engine-trace-toggle">{showTrace ? tr("Nascondi") : tr("Mostra")} {tr("passi di calcolo (")}{res.trace.length})</button>
          {showTrace && (
            <table className="w-full text-xs" data-testid="engine-trace">
              <tbody>{res.trace.map((s) => (
                <tr key={s.id} className="border-t border-slate-100 align-top">
                  <td className="py-1.5 pr-2 font-medium">{s.title}</td><td className="font-tech text-slate-600 pr-2">{s.formula}</td>
                  <td className="font-tech text-slate-500 pr-2">{Object.entries(s.inputs).map(([k, v]) => `${k}=${v.value} ${v.unit} [${v.source}]`).join("; ")}</td>
                  <td className="font-tech font-semibold whitespace-nowrap">{String(s.value)} {s.unit}</td><td className="text-slate-400">{s.source}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
          <p className="text-xs text-slate-500">{res.disclaimer}</p>
        </>
      )}
    </div>
  );
};
